import { useState, useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { AppLayout } from "@/components/layout/AppLayout";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { Search, CheckCircle2 } from "lucide-react";
import { cn } from "@/lib/utils";
import { apiRequest } from "@/lib/queryClient";
import { useAuth } from "@/lib/auth";
import { QuickAddBar } from "@/components/tasks/QuickAddBar";
import { TaskList, type TaskRow } from "@/components/tasks/TaskList";
import { TaskDetailSheet } from "@/components/tasks/TaskDetailSheet";
import { TaskAISuggestions } from "@/components/tasks/TaskAISuggestions";
import { taskLinkLabel } from "@shared/taskLinks";

/**
 * One to-do list.
 * Spec: docs/superpowers/specs/2026-09-01-tasks-reminders-unification-design.md
 *
 * Tasks and Reminders used to be two sidebar entries pointing at this page, with
 * two creation dialogs that could not see each other and "Reminders" buried as a
 * <SelectItem> in a filter dropdown. A reminder is just a task whose bell is on,
 * so there is one list, one quick-add, and a filter for the ones that ring.
 */

type Scope = "mine" | "assigned-out" | "done";

const SCOPES: { key: Scope; label: string }[] = [
  { key: "mine", label: "Mine" },
  { key: "assigned-out", label: "Assigned out" },
  { key: "done", label: "Done" },
];

export default function Tasks() {
  const { user } = useAuth();
  const [scope, setScope] = useState<Scope>("mine");
  const [search, setSearch] = useState("");
  const [ringingOnly, setRingingOnly] = useState(false);
  const [openId, setOpenId] = useState<string | null>(null);

  const { data, isLoading, isError, refetch } = useQuery<TaskRow[]>({
    queryKey: ["/api/tasks", "all"],
    queryFn: async () => (await apiRequest("GET", "/api/tasks?scope=all")).json(),
  });

  const all = data ?? [];
  const me = user?.id;

  const rows = useMemo(() => {
    let r = all;

    if (scope === "done") {
      r = r.filter((t) => t.status === "completed");
    } else {
      r = r.filter((t) => t.status !== "completed" && t.status !== "cancelled");
      r =
        scope === "assigned-out"
          ? r.filter((t) => t.userId !== me && t.createdById === me)
          : r.filter((t) => t.userId === me);
    }

    if (ringingOnly) r = r.filter((t) => t.remindAt);

    const q = search.trim().toLowerCase();
    if (q) {
      r = r.filter(
        (t) =>
          t.title.toLowerCase().includes(q) ||
          (t.description ?? "").toLowerCase().includes(q) ||
          t.links.some((l) => taskLinkLabel(l).toLowerCase().includes(q)),
      );
    }
    return r;
  }, [all, scope, me, ringingOnly, search]);

  const counts = useMemo(() => {
    const open = all.filter((t) => t.status !== "completed" && t.status !== "cancelled");
    return {
      mine: open.filter((t) => t.userId === me).length,
      "assigned-out": open.filter((t) => t.userId !== me && t.createdById === me).length,
      done: all.filter((t) => t.status === "completed").length,
      ringing: open.filter((t) => t.userId === me && t.remindAt).length,
    } as Record<string, number>;
  }, [all, me]);

  return (
    <AppLayout title="Tasks">
      <div className="mx-auto max-w-4xl space-y-4">
        <header className="flex flex-wrap items-end justify-between gap-3">
          <div>
            <div className="text-[11px] font-semibold uppercase tracking-[0.09em] text-muted-foreground">
              To-do
            </div>
            <div className="mt-1 flex items-baseline gap-2">
              <span className="text-2xl font-semibold tabular-nums">
                {isLoading ? "—" : counts.mine}
              </span>
              <span className="text-sm text-muted-foreground">
                {counts.mine === 1 ? "open item" : "open items"}
              </span>
            </div>
          </div>
        </header>

        <QuickAddBar />

        <div className="flex flex-wrap items-center gap-2">
          <div className="flex items-center gap-1 rounded-lg border border-border bg-card p-0.5">
            {SCOPES.map((s) => (
              <button
                key={s.key}
                onClick={() => setScope(s.key)}
                className={cn(
                  "flex items-center gap-1.5 rounded-md px-2.5 py-1.5 text-sm font-medium transition-colors",
                  scope === s.key
                    ? "bg-accent text-accent-foreground"
                    : "text-muted-foreground hover:bg-muted hover:text-foreground",
                )}
              >
                {s.label}
                {counts[s.key] > 0 && (
                  <span
                    className={cn(
                      "rounded px-1.5 text-xs tabular-nums",
                      scope === s.key ? "bg-white/15" : "bg-muted-foreground/12",
                    )}
                  >
                    {counts[s.key]}
                  </span>
                )}
              </button>
            ))}
          </div>

          {/* What "Reminders" used to be: the items that ring. A filter, not a
              second destination in the sidebar. */}
          <button
            onClick={() => setRingingOnly((v) => !v)}
            className={cn(
              "inline-flex h-8 items-center gap-1.5 rounded-md border px-2.5 text-sm transition-colors",
              ringingOnly
                ? "border-primary/40 bg-primary/12 text-primary-text"
                : "border-border bg-card text-muted-foreground hover:bg-muted",
            )}
          >
            Reminders
            {counts.ringing > 0 && <span className="tabular-nums">{counts.ringing}</span>}
          </button>

          <div className="relative ml-auto max-w-[220px] flex-1">
            <Search className="absolute left-2.5 top-2 h-4 w-4 text-muted-foreground" />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search…"
              className="h-8 pl-8 text-sm"
            />
          </div>
        </div>

        {isLoading ? (
          <div className="space-y-2 rounded-lg border border-border bg-card p-4">
            {[0, 1, 2, 3].map((i) => (
              <Skeleton key={i} className="h-6 w-full" />
            ))}
          </div>
        ) : isError ? (
          <div className="rounded-lg border border-border bg-card px-4 py-12 text-center">
            <p className="text-sm font-medium">Could not load your tasks.</p>
            <p className="mt-1 text-sm text-muted-foreground">The server did not answer.</p>
            <button
              onClick={() => refetch()}
              className="mt-3 text-sm font-medium text-primary-text hover:underline"
            >
              Try again
            </button>
          </div>
        ) : (
          <TaskList
            rows={rows}
            onOpen={setOpenId}
            emptyTitle={
              search || ringingOnly
                ? "Nothing matches that."
                : scope === "done"
                  ? "Nothing finished yet."
                  : scope === "assigned-out"
                    ? "You haven't delegated anything."
                    : "Nothing on your list."
            }
            emptyBody={
              search || ringingOnly
                ? "Clear the search or the Reminders filter."
                : scope === "assigned-out"
                  ? "Assign an item to someone using the person picker above."
                  : "Add one above. Give it a time and a bell if it should chase you."
            }
          />
        )}

        {scope === "mine" && !search && !ringingOnly && <TaskAISuggestions />}

        {counts.done > 0 && scope !== "done" && (
          <button
            onClick={() => setScope("done")}
            className="flex w-full items-center justify-center gap-1.5 py-2 text-xs text-muted-foreground hover:text-foreground"
          >
            <CheckCircle2 className="h-3.5 w-3.5" />
            {counts.done} completed
          </button>
        )}
      </div>

      <TaskDetailSheet id={openId} onClose={() => setOpenId(null)} />
    </AppLayout>
  );
}
